import {
  keepPreviousData,
  useMutation,
  UseMutationResult,
  useQuery,
  UseQueryResult,
  useInfiniteQuery,
  UseInfiniteQueryResult,
} from '@tanstack/react-query';
import { Block } from '@blocknote/core';
import { User } from '@novaheal/types';
import { CustomBlockContentSpec } from 'pages/in-app/worksheet/Worksheet';
import {
  WorksheetAiConfig,
  WorksheetDistributionFilter,
  WorksheetStudentsFilter,
  WorksheetStudentType,
} from 'types/Worksheets';
import { axios } from 'utils/axios';
import { AxiosError, isAxiosError } from 'axios';
import { queryClient } from 'utils/react-query';
import { logBrazeEvent } from 'utils/brazeEvents';
import { ClassUserWorksheetsResponse } from '../managament/getClasses';

export type WorksheetUserAnswersInfinitePages = {
  pageParam: number;
  pages: WorksheetUserAnswersPage[];
};

export type WorksheetUserAnswersPage = {
  data: WorksheetAnswerUserListView[];
  total: number;
  page: number;
  pageSize: number;
};

const fetchWorksheetUserAnswersPage = async ({
  pageParam = 1,
  pageSize = 10,
}: {
  pageParam?: number;
  pageSize?: number;
}): Promise<WorksheetUserAnswersPage> => {
  const { data } = await axios.get<WorksheetUserAnswersPage>('/worksheet/answers/user', {
    params: { page: pageParam, pageSize },
  });
  return data;
};

export const useInfiniteWorksheetUserAnswers = (
  pageSize = 10
): UseInfiniteQueryResult<WorksheetUserAnswersInfinitePages, unknown> => {
  return useInfiniteQuery({
    queryKey: ['worksheet-answers-user-infinite', pageSize],
    queryFn: ({ pageParam = 1 }) => fetchWorksheetUserAnswersPage({ pageParam, pageSize }),
    getNextPageParam: (lastPage) => {
      const { page, pageSize, total } = lastPage;
      const maxPage = Math.ceil(total / pageSize);
      return page < maxPage ? page + 1 : undefined;
    },
    initialPageParam: 1,
  });
};

export type Worksheet = {
  id: string;
  title: string;
  description: string;
  content: CustomBlockContentSpec[];
  createdAt: Date;
  updatedAt: Date;
  isPubliclyShared: boolean;
  createdById?: string;

  isDistributed: boolean;
  configuration?: string[];
  distributionCount?: {
    classes: number;
    guestStudents: number;
  };
  isSharedAsTemplate?: boolean;
  sharedTemplateId?: string | null;
  canDeleteTemplate?: boolean;
};

export type PaginatedWorksheetsResponse = {
  data: Worksheet[];
  total: number;
  page: number;
  pageSize: number;
  filterCounts: Record<WorksheetDistributionFilter, number>;
};

export enum WorksheetAnswerStatus {
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  SUBMITTED = 'SUBMITTED',
}

export type WorksheetSession = {
  id: string;
  title: string;
  description: string;
  content: CustomBlockContentSpec[];
  createdAt: Date;
  updatedAt: Date;

  status: WorksheetAnswerStatus | null;
};

// Base fields that are always present
type WorksheetAnswerBase = {
  id: string;
  worksheetId: string;
  title: string;
  answeredAt: Date;
  status: WorksheetAnswerStatus;
  updatedAt: Date;
  submittedAt: Date | null;
};

// Teacher list view - shows all student answers
export type WorksheetAnswerTeacherView = WorksheetAnswerBase & {
  content: CustomBlockContentSpec[];
  answeredBy: string;
  userId: string;
  createdAt: Date;
};

// User list view - minimal data for user's own answers list
export type WorksheetAnswerUserListView = WorksheetAnswerBase;

// Detail view - full answer data with worksheet info
export type WorksheetAnswerDetail = WorksheetAnswerBase & {
  content: CustomBlockContentSpec[];
  description: string;
  createdAt: Date;
};

// Union type for backward compatibility (can be removed later)
export type WorksheetAnswer =
  | WorksheetAnswerTeacherView
  | WorksheetAnswerUserListView
  | WorksheetAnswerDetail;

const getWorksheetSession = async (
  worksheetId: string | undefined
): Promise<WorksheetSession | null> => {
  if (!worksheetId) {
    return null;
  }
  const { data: worksheet } = await axios.get<WorksheetSession>(
    `/worksheet/session/${worksheetId}`
  );

  return worksheet;
};

export const useGetWorksheetSession = (
  worksheetId: string | undefined
): UseQueryResult<WorksheetSession, unknown> => {
  return useQuery({
    queryKey: [`get-worksheet-session-${worksheetId}`],
    queryFn: () => getWorksheetSession(worksheetId),
    enabled: !!worksheetId,
  });
};

export const getWorksheet = async (id: string | undefined): Promise<Worksheet | null> => {
  if (!id) {
    return null;
  }
  const { data: worksheet } = await axios.get<Worksheet>(`/worksheet/${id}`);

  return worksheet;
};

export const useGetWorksheet = (id: string | undefined): UseQueryResult<Worksheet, unknown> => {
  return useQuery({
    queryKey: [`get-worksheet-${id}`],
    queryFn: () => getWorksheet(id),
    enabled: !!id,
  });
};

const createWorksheet = async (): Promise<Worksheet> => {
  const { data: worksheet } = await axios.post<Worksheet>(`/worksheet`);

  return worksheet;
};

export const useCreateWorksheet = (): UseMutationResult<Worksheet, unknown, unknown, unknown> => {
  return useMutation({
    mutationKey: ['create-worksheet'],
    mutationFn: () => createWorksheet(),
    onSuccess: () => {
      logBrazeEvent('worksheet_created');
    },
  });
};

const getWorksheets = async (
  filter?: WorksheetDistributionFilter,
  page?: number,
  search?: string
): Promise<PaginatedWorksheetsResponse> => {
  const params: {
    distributionFilter?: WorksheetDistributionFilter;
    page?: number;
    search?: string;
  } = {};

  // Add distribution filter if provided
  if (filter) {
    params.distributionFilter = filter;
  }

  // Add page if provided
  if (page) {
    params.page = page;
  }

  // Add search if provided
  if (search) {
    params.search = search;
  }

  const { data } = await axios.get<PaginatedWorksheetsResponse>('/worksheet', { params });

  return data;
};

export const useGetWorksheets = (
  filter?: WorksheetDistributionFilter,
  page?: number,
  search?: string
): UseQueryResult<PaginatedWorksheetsResponse, unknown> => {
  return useQuery({
    queryKey: [`worksheets`, filter, page, search],
    queryFn: () => getWorksheets(filter, page, search),
    placeholderData: keepPreviousData,
  });
};

// Fetch worksheets filtered by related article
const getWorksheetsByArticle = async (articleId: string): Promise<Worksheet[]> => {
  const { data: worksheets } = await axios.get<Worksheet[]>(`/worksheet`, {
    params: { articleId },
  });

  return worksheets;
};

export const useGetWorksheetsByArticle = (
  articleId?: string
): UseQueryResult<Worksheet[], unknown> => {
  return useQuery({
    queryKey: [`worksheets-by-article`, articleId],
    queryFn: () => getWorksheetsByArticle(articleId as string),
    enabled: !!articleId,
  });
};

const getDistributedWorksheets = async (): Promise<PaginatedWorksheetsResponse> => {
  const { data: worksheets } = await axios.get<PaginatedWorksheetsResponse>(`/worksheet`, {
    params: {
      distributionFilter: WorksheetDistributionFilter.DISTRIBUTED,
    },
  });

  return worksheets;
};

export const useGetDistributedWorksheets = (): UseQueryResult<
  PaginatedWorksheetsResponse,
  unknown
> => {
  return useQuery({
    queryKey: [`worksheets-distributed`],
    queryFn: getDistributedWorksheets,
  });
};

const getWorksheetAnswers = async (): Promise<WorksheetAnswerTeacherView[]> => {
  const { data: worksheetAnswers } =
    await axios.get<WorksheetAnswerTeacherView[]>(`/worksheet/answers`);

  return worksheetAnswers;
};

const getWorksheetUserAnswers = async (): Promise<WorksheetAnswerUserListView[]> => {
  const { data: worksheetAnswers } =
    await axios.get<WorksheetAnswerUserListView[]>(`/worksheet/answers/user`);

  return worksheetAnswers;
};

const getWorksheetUserAnswersById = async (id: string): Promise<WorksheetAnswerDetail | null> => {
  const { data: worksheetAnswers } = await axios.get<WorksheetAnswerDetail>(
    `/worksheet/answers/${id}/worksheet`
  );

  return worksheetAnswers;
};

export const useGetWorksheetAnswers = (): UseQueryResult<WorksheetAnswer[], unknown> => {
  return useQuery({
    queryKey: [`worksheet-answers`],
    queryFn: getWorksheetAnswers,
  });
};

export const useGetWorksheetUserAnswers = (): UseQueryResult<WorksheetAnswer[], unknown> => {
  return useQuery({
    queryKey: [`worksheet-answers-user`],
    queryFn: getWorksheetUserAnswers,
  });
};

export const useGetWorksheetUserAnswersById = (
  id: string | undefined
): UseQueryResult<WorksheetAnswerDetail | null, unknown> => {
  return useQuery({
    queryKey: [`worksheet-answers-user-${id}`],
    queryFn: () => getWorksheetUserAnswersById(id as string),
    enabled: !!id,
    retry: false,
  });
};

export type SaveWorksheetOptions = {
  id: string;
  title: string;
  description: string;
  content: Block[];
  intent: WorksheetSaveIntent;
};

export enum WorksheetSaveIntent {
  AUTO_SAVE = 'AUTO_SAVE',
  MANUAL_SAVE = 'MANUAL_SAVE',
  MANUAL_SAVE_DELETE_ANSWERS = 'MANUAL_SAVE_DELETE_ANSWERS',
  MANUAL_SAVE_PROPAGATE_TEMPLATE = 'MANUAL_SAVE_PROPAGATE_TEMPLATE',
  MANUAL_SAVE_DELETE_ANSWERS_PROPAGATE_TEMPLATE = 'MANUAL_SAVE_DELETE_ANSWERS_PROPAGATE_TEMPLATE',
}

const saveWorksheet = async (options: SaveWorksheetOptions): Promise<Worksheet> => {
  const { id, title, description, content, intent } = options;
  const { data: worksheet } = await axios.post<Worksheet>(`/worksheet/${id}`, {
    title,
    description,
    content,
    intent,
  });

  return worksheet;
};

export const useSaveWorksheet = (): UseMutationResult<
  Worksheet,
  unknown,
  SaveWorksheetOptions,
  unknown
> => {
  return useMutation({
    meta: { suppressGlobalMutationError: true },
    retry(failureCount, error) {
      const axiosError = error as AxiosError;
      if (axiosError.response?.status === 409) {
        return false;
      }
      return failureCount < 3;
    },
    mutationKey: ['save-worksheet'],
    mutationFn: (options) => saveWorksheet(options),
  });
};

const deleteWorksheet = async (id: string): Promise<void> => {
  await axios.delete<void>(`/worksheet/${id}`);
};

export const useDeleteWorksheet = (): UseMutationResult<void, unknown, string, unknown> => {
  return useMutation({
    mutationKey: ['delete-worksheet'],
    mutationFn: (id) => deleteWorksheet(id),
  });
};

const duplicateWorksheet = async (id: string, modifiedContent?: Block[]): Promise<Worksheet> => {
  const { data: newWorksheet } = await axios.post<Worksheet>(`/worksheet/duplicate/${id}`, {
    ...(modifiedContent && { modifiedContent }),
  });
  return newWorksheet;
};

export const useDuplicateWorksheet = (): UseMutationResult<
  Worksheet,
  unknown,
  {
    id: string;
    modifiedContent?: Block[];
  },
  unknown
> => {
  return useMutation({
    mutationKey: ['duplicate-worksheet'],
    mutationFn: ({ id, modifiedContent }) => duplicateWorksheet(id, modifiedContent),
  });
};

type SaveWorksheetAnswersResponse = {
  success: boolean;
  status: WorksheetAnswerStatus;
};

const saveWorksheetAnswers = async (options: {
  id: string;
  content: Block[];
  progressInPercent: number;
}): Promise<{
  success: boolean;
  status: WorksheetAnswerStatus;
}> => {
  const { id, content, progressInPercent } = options;
  const { data } = await axios.post<SaveWorksheetAnswersResponse>(`/worksheet/answers/${id}`, {
    content,
    progressInPercent,
  });

  return data;
};

export const useSaveWorksheetAnswers = (): UseMutationResult<
  SaveWorksheetAnswersResponse,
  unknown,
  { id: string; content: Block[]; progressInPercent: number },
  unknown
> => {
  return useMutation({
    mutationKey: ['save-worksheet-answers'],
    mutationFn: (options) => saveWorksheetAnswers(options),
  });
};

const finalSubmitWorksheet = async (options: {
  id: string;
  content: Block[];
  progressInPercent: number;
}): Promise<SaveWorksheetAnswersResponse> => {
  const { id, content, progressInPercent } = options;
  const { data } = await axios.post<SaveWorksheetAnswersResponse>(
    `/worksheet/answers/${id}/final-submit`,
    {
      content,
      progressInPercent,
    }
  );

  return data;
};

export const useFinalSubmitWorksheet = (): UseMutationResult<
  SaveWorksheetAnswersResponse,
  unknown,
  { id: string; content: Block[]; progressInPercent: number },
  unknown
> => {
  return useMutation({
    mutationKey: ['final-submit-worksheet'],
    mutationFn: (options) => finalSubmitWorksheet(options),
  });
};

const getWorksheetAnswerByUserAndWorksheet = async (
  worksheetId: string | undefined,
  userId: string | undefined
): Promise<WorksheetAnswerDetail | null> => {
  if (!worksheetId || !userId) {
    throw new Error('Worksheet ID and User ID are required');
  }
  const { data: worksheetAnswer } = await axios.get<WorksheetAnswerDetail | null>(
    `/worksheet/answers/${worksheetId}/user/${userId}`
  );

  return worksheetAnswer;
};

export const useGetWorksheetAnswerByUserAndWorksheet = (
  worksheetId: string | undefined,
  userId: string | undefined
): UseQueryResult<WorksheetAnswerDetail | null, unknown> => {
  return useQuery({
    queryKey: [`worksheet-answer-${worksheetId}-user-${userId}`],
    queryFn: () => getWorksheetAnswerByUserAndWorksheet(worksheetId, userId),
    enabled: !!worksheetId && !!userId,
  });
};

type WorksheetReviewData = {
  id: string;
  title: string;
  content: CustomBlockContentSpec[];
};

const getWorksheetReviewData = async (id: string | undefined): Promise<WorksheetReviewData> => {
  if (!id) {
    throw new Error('Worksheet ID is required');
  }
  const { data: worksheet } = await axios.get<Worksheet>(`/worksheet/review/${id}`, {});

  return worksheet;
};

export const useGetWorksheetReviewData = (
  id: string | undefined
): UseQueryResult<WorksheetReviewData, unknown> => {
  return useQuery({
    queryKey: [`worksheet-review-${id}`],
    queryFn: () => getWorksheetReviewData(id),
    enabled: !!id,
  });
};

export type WorksheetParticipant = {
  worksheetId: string;
  userData: Pick<User, 'firstName' | 'lastName' | 'id' | 'email'>;
  /** Set when user has started or submitted an answer; null for class members not yet started */
  answeredAt?: Date | null;
  status?: WorksheetAnswerStatus;
  updatedAt?: Date | null;
  submittedAt?: Date | null;
};

const getWorksheetParticipants = async (
  worksheetId: string | undefined,
  classId: string | undefined
): Promise<WorksheetParticipant[]> => {
  if (!worksheetId) {
    throw new Error('Worksheet ID is required');
  }
  const params: Record<string, string> = {};
  if (classId) params.classId = classId;
  const { data: worksheet } = await axios.get<WorksheetParticipant[]>(
    `/worksheet/review/participants/${worksheetId}`,
    { params: Object.keys(params).length ? params : undefined }
  );

  return worksheet;
};

export const useGetWorksheetParticipants = (
  id: string | undefined,
  classId: string | undefined
): UseQueryResult<WorksheetParticipant[], unknown> => {
  return useQuery({
    queryKey: [`worksheet-participants-${id}`, classId],
    queryFn: () => getWorksheetParticipants(id, classId),
    enabled: !!id,
  });
};

export type WorksheetReviewStats = {
  participantCount: number;
  submittedCount: number;
  averageSelfAssessmentScore: number | null;
};

const getWorksheetReviewStats = async (
  worksheetId: string | undefined,
  classId: string | undefined
): Promise<WorksheetReviewStats> => {
  if (!worksheetId) {
    throw new Error('Worksheet ID is required'); // safeguard - query is disabled without id
  }
  const params: Record<string, string> = {};
  if (classId) params.classId = classId;
  const { data } = await axios.get<WorksheetReviewStats>(`/worksheet/review/stats/${worksheetId}`, {
    params: Object.keys(params).length ? params : undefined,
  });
  return data;
};

export const useGetWorksheetReviewStats = (
  id: string | undefined,
  classId: string | undefined
): UseQueryResult<WorksheetReviewStats, unknown> => {
  return useQuery({
    queryKey: [`worksheet-review-stats-${id}`, classId],
    queryFn: () => getWorksheetReviewStats(id, classId),
    enabled: !!id,
  });
};

const generatePublicLink = async (
  worksheetId?: string
): Promise<{ shareLink: string; shareToken: string }> => {
  if (!worksheetId) {
    throw new Error('Worksheet ID is required');
  }
  const { data } = await axios.post<{ shareLink: string; shareToken: string }>(
    `/worksheet/share/${worksheetId}/generate-link`
  );
  return data;
};

export const useGeneratePublicLink = (
  worksheetId?: string
): UseMutationResult<{ shareLink: string; shareToken: string }, unknown, void, unknown> => {
  return useMutation({
    mutationFn: () => generatePublicLink(worksheetId),
  });
};

const getWorksheetShareLink = async (
  worksheetId?: string
): Promise<{ shareLink: string } | null> => {
  if (!worksheetId) {
    throw new Error('Worksheet ID is required');
  }
  try {
    const { data } = await axios.get<{ shareLink: string }>(`/worksheet/share/${worksheetId}/link`);
    return data;
  } catch (error) {
    // If no share link exists, return null
    return null;
  }
};

export const useGetWorksheetShareLink = (
  worksheetId?: string
): UseQueryResult<{ shareLink: string } | null, unknown> => {
  return useQuery({
    queryKey: [`worksheet-share-link-${worksheetId}`],
    queryFn: () => getWorksheetShareLink(worksheetId),
    enabled: !!worksheetId,
  });
};

const copyWorksheetFromToken = async (token: string): Promise<Worksheet> => {
  const { data } = await axios.get<Worksheet>(`/worksheet/share/${token}`);
  return data;
};

export const useCopyWorksheetFromToken = (): UseMutationResult<
  Worksheet,
  unknown,
  string,
  unknown
> => {
  return useMutation({
    mutationFn: copyWorksheetFromToken,
  });
};

const inviteUserToWorksheet = async (params: {
  worksheetId?: string;
  email: string;
}): Promise<void> => {
  if (!params.worksheetId) {
    throw new Error('Worksheet ID is required');
  }
  await axios.post(`/worksheet/share/${params.worksheetId}/invite`, {
    email: params.email,
  });
};

export const useInviteUserToWorksheet = (
  worksheetId?: string
): UseMutationResult<void, unknown, string, unknown> => {
  return useMutation({
    mutationFn: (email: string) => inviteUserToWorksheet({ worksheetId, email }),
  });
};

export type AiWorksheetRunStatus = {
  runId: string;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  progress: number;
  progressMessage: string;
  /** Id of the generated worksheet once the run is COMPLETED */
  resultId: string | null;
  failureReason: string | null;
};

type CreateAiWorksheetFromArticleParams = {
  articleId: string;
  config?: WorksheetAiConfig;
};

const createAiWorksheetFromArticle = async (
  params: CreateAiWorksheetFromArticleParams
): Promise<{ runId: string }> => {
  const { articleId, config } = params;
  const { data } = await axios.post<{ runId: string }>(`/worksheet/ai/${articleId}`, config);

  return data;
};

export const useCreateAiWorksheetFromArticle = (): UseMutationResult<
  { runId: string },
  unknown,
  CreateAiWorksheetFromArticleParams,
  unknown
> => {
  return useMutation({
    mutationKey: ['create-ai-worksheet-from-article'],
    mutationFn: createAiWorksheetFromArticle,
  });
};

const getAiWorksheetRunStatus = async (runId: string): Promise<AiWorksheetRunStatus> => {
  const { data } = await axios.get<AiWorksheetRunStatus>(`/worksheet/ai/run/${runId}`);

  return data;
};

export const useGetAiWorksheetRunStatus = (): UseMutationResult<
  AiWorksheetRunStatus,
  unknown,
  string,
  unknown
> => {
  return useMutation({
    mutationKey: ['get-ai-worksheet-run-status'],
    mutationFn: getAiWorksheetRunStatus,
  });
};

export type WorksheetStudent = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  worksheetCount: number;
  assignedWorksheetCount: number;
  memberOfClasses: { id: string; name: string }[];
  type: WorksheetStudentType;
};

export type PaginatedWorksheetStudentsResponse = {
  data: WorksheetStudent[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  filterCounts: Record<WorksheetStudentsFilter, number>;
};

// Fetch students for a worksheet (with pagination)
const getWorksheetStudents = async ({
  filter,
  page = 1,
  pageSize = 10,
  searchQuery,
}: {
  filter?: WorksheetStudentsFilter;
  page?: number;
  pageSize?: number;
  searchQuery?: string;
}): Promise<PaginatedWorksheetStudentsResponse> => {
  const { data } = await axios.get<PaginatedWorksheetStudentsResponse>(`/worksheet/student`, {
    params: { page, pageSize, filter, searchQuery },
  });
  return data;
};

/**
 * Fetch students for a worksheet (paginated).
 * @param worksheetId Worksheet ID
 * @param page Page number (default 1)
 * @param pageSize Page size (default 10)
 */

export const useGetWorksheetStudents = ({
  filter,
  page = 1,
  pageSize = 10,
  searchQuery,
}: {
  filter?: WorksheetStudentsFilter;
  page?: number;
  pageSize?: number;
  searchQuery?: string;
}): UseQueryResult<PaginatedWorksheetStudentsResponse, unknown> => {
  return useQuery({
    queryKey: ['worksheet-students', page, pageSize, filter, searchQuery],
    queryFn: () => getWorksheetStudents({ page, pageSize, filter, searchQuery }),
    enabled: true,
    placeholderData: keepPreviousData,
  });
};

// Fetch worksheet students distributed by teacher
const getWorksheetsByStudent = async ({
  studentId,
  page = 1,
  pageSize = 10,
}: {
  studentId?: string;
  page?: number;
  pageSize?: number;
}): Promise<ClassUserWorksheetsResponse> => {
  const { data } = await axios.get<ClassUserWorksheetsResponse>(`/worksheet/student/${studentId}`, {
    params: { page, pageSize },
  });
  return data;
};

/**
 * Fetch students for a worksheet (paginated).
 * @param worksheetId Worksheet ID
 * @param page Page number (default 1)
 * @param pageSize Page size (default 10)
 */

export const useGetWorksheetsByStudent = ({
  studentId,
  page = 1,
  pageSize = 10,
}: {
  studentId?: string;
  page?: number;
  pageSize?: number;
}): UseQueryResult<ClassUserWorksheetsResponse, unknown> => {
  return useQuery({
    queryKey: ['worksheet-students', page, pageSize, studentId],
    queryFn: () => getWorksheetsByStudent({ page, pageSize, studentId }),
    enabled: !!studentId,
  });
};

// ============ WORKSHEET FEEDBACK SUMMARY ============

/** Matches backend Prisma enum `WorksheetFeedbackSummaryRating` */
export type WorksheetFeedbackSummaryRating = 'not_helpful' | 'helpful';

export const WorksheetFeedbackSummaryRating = {
  NOT_HELPFUL: 'not_helpful',
  HELPFUL: 'helpful',
} as const satisfies Record<string, WorksheetFeedbackSummaryRating>;

/**
 * Maps thumbs-up labels to backend `WORKSHEET_FEEDBACK_SUMMARY_TAGS` values.
 * Values must stay aligned with backend DTO validation.
 */
export enum WorksheetFeedbackPositiveSummaryTag {
  DETAILED = 'detailed',
  WELL_STRUCTURED = 'well_structured',
  TIME_SAVING = 'time_saving',
  UNDERSTANDABLE = 'understandable',
  HELPFUL = 'helpful',
  CONCISE = 'concise',
}

/** Matches backend `WORKSHEET_FEEDBACK_SUMMARY_TAGS` for thumbs-down feedback. */
export enum WorksheetFeedbackNegativeSummaryTag {
  SHORT = 'short',
  INCORRECT = 'incorrect',
  BLURRY = 'blurry',
  SUPERFICIAL = 'superficial',
  CONFUSING = 'confusing',
  OVERLOADED = 'overloaded',
}

export type WorksheetFeedbackSummaryTag =
  | WorksheetFeedbackPositiveSummaryTag
  | WorksheetFeedbackNegativeSummaryTag;

/**
 * Shared React Query cache key for feedback summaries.
 *
 * Why it exists: the summary tab loads a cached summary on mount and when the
 * class filter changes (GET). Regenerating or rating updates that same summary
 * in the UI without a second request — each scope (worksheet-wide vs class)
 * needs its own cache slot so switching "Alle Teilnehmenden" ↔ a class does not
 * show the wrong summary.
 *
 * Used by useGetWorksheetFeedbackSummary (read), useStartFeedbackSummaryRun and
 * useRateFeedbackSummary (write; invalidates/updates the cached summary).
 *
 * `classId ?? null` normalizes "all participants" (undefined) to null; React
 * Query treats undefined and null as different key segments.
 */
export const worksheetFeedbackSummaryQueryKey = (
  worksheetId: string | undefined,
  classId: string | undefined
): [string, string | undefined, string | null] => [
  'worksheet-feedback-summary',
  worksheetId,
  classId ?? null,
];

export type WorksheetFeedbackSummaryEntry = {
  heading: string;
  description: string;
};

export type WorksheetFeedbackSummary = {
  id: string;
  worksheetId: string;
  classId: string | null;
  participantCount: number;
  overallAssessment: string;
  mainIssues: WorksheetFeedbackSummaryEntry[];
  potentialReasons: WorksheetFeedbackSummaryEntry[];
  generatedAt: Date;
  generatedBy: {
    id: string;
    firstName: string;
    lastName: string;
  };
  feedback?: {
    id: string;
    rating: WorksheetFeedbackSummaryRating;
    ratedAt: Date;
    ratedById: string | null;
    message: string | null;
    shortMessages: string[];
  };
  submissionsAtGeneration: number;
  currentSubmissionCount: number;
  isStale: boolean;
};

export const getWorksheetFeedbackSummary = async (
  worksheetId: string,
  classId: string | undefined
): Promise<WorksheetFeedbackSummary | null> => {
  const params: Record<string, string> = {};
  if (classId) params.classId = classId;

  try {
    const { data } = await axios.get<WorksheetFeedbackSummary>(
      `/worksheet/feedback/${worksheetId}/summary`,
      { params: Object.keys(params).length ? params : undefined }
    );
    return data;
  } catch (error) {
    // If no summary exists, return null. This will trigger the UI to show the "Generate Summary" button.
    if (isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

/** Loads a cached summary only; does not trigger AI generation. */
export const useGetWorksheetFeedbackSummary = (
  worksheetId: string | undefined,
  classId: string | undefined
): UseQueryResult<WorksheetFeedbackSummary | null, unknown> => {
  return useQuery({
    queryKey: worksheetFeedbackSummaryQueryKey(worksheetId, classId),
    queryFn: () => getWorksheetFeedbackSummary(worksheetId as string, classId),
    enabled: !!worksheetId,
  });
};

/** Starts an async AI summary generation run for the worksheet scope. */
export type FeedbackSummaryRunStatus = {
  runId: string;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  progress: number;
  progressMessage: string;
  /** Id of the generated summary once the run is COMPLETED */
  resultId: string | null;
  failureReason: string | null;
};

const startFeedbackSummaryRun = async ({
  worksheetId,
  classId,
}: {
  worksheetId: string;
  classId?: string;
}): Promise<{ runId: string }> => {
  const { data } = await axios.post<{ runId: string }>(
    `/worksheet/feedback/${worksheetId}/summary`,
    classId ? { classId } : {}
  );
  return data;
};

export const useStartFeedbackSummaryRun = (): UseMutationResult<
  { runId: string },
  AxiosError,
  { worksheetId: string; classId?: string }
> => {
  return useMutation({
    mutationFn: startFeedbackSummaryRun,
  });
};

const getFeedbackSummaryRunStatus = async (runId: string): Promise<FeedbackSummaryRunStatus> => {
  const { data } = await axios.get<FeedbackSummaryRunStatus>(
    `/worksheet/feedback/summary/run/${runId}`
  );
  return data;
};

export const useGetFeedbackSummaryRunStatus = (): UseMutationResult<
  FeedbackSummaryRunStatus,
  AxiosError,
  string
> => {
  return useMutation({
    mutationFn: (runId: string) => getFeedbackSummaryRunStatus(runId),
  });
};

/**
 * Rate a feedback summary
 */
export type SubmitFeedbackSummaryPayload = {
  summaryId: string;
  rating: WorksheetFeedbackSummaryRating;
  shortMessages?: WorksheetFeedbackSummaryTag[];
  message?: string;
};

export type RateFeedbackSummaryPayload = SubmitFeedbackSummaryPayload & {
  worksheetId: string;
  classId?: string;
};

export const useRateFeedbackSummary = (): UseMutationResult<
  void,
  AxiosError,
  RateFeedbackSummaryPayload
> => {
  return useMutation({
    mutationFn: async ({ summaryId, rating, message, shortMessages }) => {
      await axios.post(`/worksheet/feedback/summary/${summaryId}/rate`, {
        rating,
        message,
        shortMessages,
      });
    },
    onSuccess: (_data, { worksheetId, classId }) => {
      queryClient.invalidateQueries({
        queryKey: worksheetFeedbackSummaryQueryKey(worksheetId, classId),
      });
    },
  });
};
